import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronRight, TrendingUp, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';

interface MonthlyData {
  month: string;
  receitas: number;
  despesas: number;
}

interface FinancialChartWidgetProps {
  isLoaded?: boolean;
  compact?: boolean;
}

export function FinancialChartWidget({ isLoaded = true, compact = false }: FinancialChartWidgetProps) {
  const navigate = useNavigate();
  const [chartData, setChartData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showChart, setShowChart] = useState(false);

  useEffect(() => {
    fetchFinancialHistory();
  }, []);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setShowChart(true), 300);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const fetchFinancialHistory = async () => {
    try {
      const months: MonthlyData[] = [];
      const today = new Date();
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
        const endDate = new Date(year, month, 0).toISOString().split('T')[0];
        
        const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        
        const [receitasRes, despesasRes] = await Promise.all([
          supabase
            .from('financeiro_usuario')
            .select('valor')
            .eq('tipo', 'recebimento')
            .gte('data', startDate)
            .lte('data', endDate),
          supabase
            .from('financeiro_usuario')
            .select('valor')
            .eq('tipo', 'despesa')
            .gte('data', startDate)
            .lte('data', endDate)
        ]);

        const receitas = receitasRes.data?.reduce((sum, item) => sum + Number(item.valor), 0) || 0;
        const despesas = despesasRes.data?.reduce((sum, item) => sum + Number(item.valor), 0) || 0;
        
        months.push({
          month: monthNames[month - 1],
          receitas: receitas / 1000,
          despesas: despesas / 1000,
        });
      }
      
      setChartData(months);
    } catch (error) {
      console.error('Error fetching financial history:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalReceitas = chartData.reduce((sum, d) => sum + d.receitas, 0);
  const totalDespesas = chartData.reduce((sum, d) => sum + d.despesas, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <Card className="bento-card h-full overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
              >
                <Wallet className="h-5 w-5 text-primary" />
              </motion.div>
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                Fluxo de Caixa
              </motion.span>
            </CardTitle>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => navigate('/financeiro')}
              >
                Detalhes
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </motion.div>
          </div>
          <motion.div 
            className="flex items-center gap-4 mt-1"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="flex items-center gap-1.5">
              <motion.div 
                className="w-2.5 h-2.5 rounded-full bg-emerald-500"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.9, type: "spring" }}
              />
              <span className="text-xs text-muted-foreground">Receitas</span>
              <motion.span 
                className="text-xs font-semibold text-emerald-600"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
              >
                R$ {totalReceitas.toFixed(1)}k
              </motion.span>
            </div>
            <div className="flex items-center gap-1.5">
              <motion.div 
                className="w-2.5 h-2.5 rounded-full bg-rose-500"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.95, type: "spring" }}
              />
              <span className="text-xs text-muted-foreground">Despesas</span>
              <motion.span 
                className="text-xs font-semibold text-rose-600"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.05 }}
              >
                R$ {totalDespesas.toFixed(1)}k
              </motion.span>
            </div>
          </motion.div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className={`${compact ? 'h-[160px]' : 'h-[200px]'} flex items-center justify-center`}>
              <motion.div 
                className="text-muted-foreground text-sm"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                Carregando...
              </motion.div>
            </div>
          ) : chartData.length > 0 && (totalReceitas > 0 || totalDespesas > 0) ? (
            <motion.div 
              className={`${compact ? 'h-[160px]' : 'h-[200px]'} mt-2`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: showChart ? 1 : 0, scale: showChart ? 1 : 0.95 }}
              transition={{ duration: 0.5 }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorReceitas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorDespesas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis 
                    dataKey="month" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `${value}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value: number) => [`R$ ${value.toFixed(1)}k`, '']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="receitas" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorReceitas)" 
                    name="Receitas"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="despesas" 
                    stroke="#f43f5e" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorDespesas)" 
                    name="Despesas"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </motion.div>
          ) : (
            <motion.div 
              className={`${compact ? 'h-[160px]' : 'h-[200px]'} flex flex-col items-center justify-center text-center`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.div 
                className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mb-3"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring" }}
              >
                <TrendingUp className="h-6 w-6 text-muted-foreground/50" />
              </motion.div>
              <p className="text-sm text-muted-foreground mb-1">Sem dados financeiros</p>
              <p className="text-xs text-muted-foreground/70">Registre receitas e despesas para visualizar</p>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
